import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { useDispatch } from 'react-redux'
import { orderBy as sortBy } from 'lodash'
import { sortKey } from '../../helpers/sortKey'
import {
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton as MuiIconButton
} from '@mui/material'
import Close from '@mui/icons-material/Close'
import MemberReportIcon from '../icons/MemberReportIcon'
import MemberReportDialog from '../MemberReportDialog'
import { styled } from '@mui/material/styles'
import SearchInput from '../SearchInput'
import { defineMessages, useIntl } from 'react-intl'
import RefreshButton from '../buttons/RefreshButton'
import DeleteButton from '../buttons/DeleteButton'
import DataTable from '../DataTable'
import IconButton from '../buttons/IconButton'
import MikeSlidingPanel from '../SlidingPanel'
import MikeTooltip from '@mike/mike-shared-frontend/mike-tooltip/MikeTooltip'
import {
  deleteUsers,
  inviteUser,
  loadUsers,
  updateUser
} from '../../actions/users'

import LongName from '../DataTable/renders/LongName'
import messages from '../../shared/messages'
import { css } from '@emotion/css'
import ConfirmationDialog from '../Dialog/ConfirmationDialog'
import LongText from '../DataTable/renders/LongText'
import UserInviteForm, {
  UserInviteType
} from '../UserInviteForm/UserInviteForm'
import UserEditForm from '../UserEditForm'
import UsersNav from '../UsersNav'

import { ReactComponent as UserOwner } from '@mike/mike-shared-frontend/media/icons/UserOwner'
import { ReactComponent as UserContributor } from '@mike/mike-shared-frontend/media/icons/UserContributor'
import { ReactComponent as UserReader } from '@mike/mike-shared-frontend/media/icons/UserReader'
import ITenantUser from '../../model/ITenantUser'
import { useTypedSelector } from '../../reducers'
import { setMemberReport } from '../../reducers/users'
import { getIsCurrentTenantOwner } from '../../state'
import { useTablePageParam } from '../../hooks/use-table-page-param.hook'
import { buildUserTableActions } from './userTableActions'

const localMessages = defineMessages({
  title: {
    id: 'screens.users.title'
  },
  folderTitle: {
    id: 'screens.users.folderTitle'
  },
  // Folder-scoped only: the tenant-wide /site-access/users screen lists every
  // user in the tenant, not the members of a folder.
  folderDescription: {
    id: 'projectAccess.nav.users.description'
  },
  confirmationDialogTitle: {
    id: 'screens.users.confirmationDialog.title'
  },
  confirmationDialogMessage: {
    id: 'screens.users.confirmationDialog.message'
  },
  searchPlaceholder: {
    id: 'screens.users.input.search.placeholder'
  }
})

const hoverStyle = () => {
  return css`
    &:hover {
      cursor: pointer;
    }
  `
}

const StyledTableTopHeader = styled('div')(({ theme }) => ({
  paddingLeft: theme.spacing(4),
  paddingRight: theme.spacing(4),
  paddingTop: theme.spacing(4),
  paddingBottom: theme.spacing(2),
  backgroundColor: '#f2f5f7'
}))

const StyledTableTopActionButtons = styled('div')(({ theme }) => ({
  display: 'flex',
  alignItems: 'flex-end',
  justifyContent: 'space-between',
  paddingTop: theme.spacing(1),
  paddingBottom: theme.spacing(3)
}))

const StyledLeftActionButtons = styled('div')({
  display: 'flex',
  alignItems: 'flex-end',
  justifyContent: 'flex-start'
})

const StyledRightActionButtons = styled('div')({
  gap: '0.5rem',
  display: 'flex',
  alignItems: 'flex-end',
  justifyContent: 'flex-end'
})

const Users = (props: {
  projectId?: string
  hideNav?: boolean
  formsInDialog?: boolean
  pageParamName?: string
  disableRowClick?: boolean
  canManageMembers?: boolean
  [key: string]: any
}) => {
  const {
    projectId: projectIdProp,
    hideNav,
    formsInDialog,
    pageParamName,
    disableRowClick,
    canManageMembers
  } = props
  const tenantId = useTypedSelector(state => state['auth'].user?.tenantId)
  const currentUserId = useTypedSelector(state => state['auth'].user?.id)
  const effectiveId = projectIdProp || tenantId
  const users = useTypedSelector(state => state['users'].users)
  const isLoadingUsers = useTypedSelector(state => state['users'].loadingUsers)
  const dispatch = useDispatch()
  const intl = useIntl()
  const [searchText, setSearchText] = useState<string>('')
  const [openUserEdit, setOpenUserEdit] = useState(false)
  const [inviteUserDialogVisible, setInviteUserDialogVisible] = useState(false)
  const memberReportOpen = useTypedSelector(
    state => state['users'].memberReport.dialogOpen
  )
  const [selectedRows, setSelectedRows] = useState<ITenantUser[]>([])
  const [sortOrder, setSortOrder] = useState<string[]>(['asc'])
  const [sortOrderBy, setSortOrderBy] = useState<string[]>(['name'])
  const [openDeleteConfirmation, setOpenDeleteConfirmation] = useState<boolean>(
    false
  )
  const [itemsToBeDeleted, setItemsToBeDeleted] = useState<ITenantUser[]>([])
  const [
    currentEditingUser,
    setCurrentEditingUser
  ] = useState<ITenantUser | null>(null)
  const [page, setPage] = useTablePageParam(pageParamName)
  const [rowsPerPage, setRowsPerPage] = useState<number>(
    () => parseInt(localStorage.getItem('users-rowsPerPage') ?? '10', 10) || 10
  )

  useEffect(() => {
    if (effectiveId) {
      dispatch(loadUsers(effectiveId))
    }
  }, [dispatch, effectiveId])

  const onRefresh = () => {
    if (effectiveId) {
      dispatch(loadUsers(effectiveId))
    }
  }

  const onDelete = useCallback((item: ITenantUser) => {
    setItemsToBeDeleted([item])
    setOpenDeleteConfirmation(true)
  }, [])

  const onDeleteItems = () => {
    setItemsToBeDeleted(selectedRows)
    setOpenDeleteConfirmation(true)
  }

  const onColumnClick = useCallback((column: ITenantUser) => {
    setCurrentEditingUser(column)
    setOpenUserEdit(true)
  }, [])

  const folderScoped = Boolean(projectIdProp)
  // The access report lists every member's email and role: site owners only.
  const canViewMemberReport =
    useTypedSelector(getIsCurrentTenantOwner) && !folderScoped && !!tenantId
  const canManageUsers = !folderScoped || !!canManageMembers
  // Shared by the row menu and the toolbar. Nobody can remove themselves.
  const canDeleteUser = useCallback(
    (user: ITenantUser) => canManageUsers && user.id !== currentUserId,
    [canManageUsers, currentUserId]
  )

  const tableActions = useMemo(
    () =>
      buildUserTableActions({
        canEdit: canManageUsers,
        canDeleteUser,
        onEdit: onColumnClick,
        onDelete
      }),
    [canManageUsers, canDeleteUser, onColumnClick, onDelete]
  )

  const onHandleRequestSort = (orderBy: string, order: string) => {
    setSortOrder([order])
    setSortOrderBy([orderBy])
    setPage(0)
  }

  const sortedUsers: ITenantUser[] = useMemo(() => {
    const filteredUsers =
      searchText.length === 0
        ? users
        : users.filter(user => {
            return (
              (user.name ?? '')
                .toLowerCase()
                .includes(searchText.toLowerCase()) ||
              (user.email ?? '')
                .toLowerCase()
                .includes(searchText.toLowerCase())
            )
          })

    // DataTable leaves ordering to the caller once totalCount is set.
    return sortBy(
      filteredUsers,
      sortOrderBy.map(field => (user: ITenantUser) => sortKey(user[field])),
      sortOrder as ('asc' | 'desc')[]
    )
  }, [users, searchText, sortOrderBy, sortOrder])

  const onOkDeleteUsers = () => {
    if (effectiveId) {
      if (itemsToBeDeleted.length > 0) {
        dispatch(deleteUsers(itemsToBeDeleted, effectiveId))
      }
    }
    setSelectedRows([])
    setOpenDeleteConfirmation(false)
  }

  const onCancelDeleteUsers = () => {
    setOpenDeleteConfirmation(false)
  }

  // Only offer the pointer cursor where the row itself is a click target.
  const rowClassName = disableRowClick ? undefined : hoverStyle

  const longTextRender = value => <LongText longText={value || ''} />

  const projectNameRender = value => <LongName longName={value} />

  const accessLevelRender = value => {
    switch (value) {
      case 'Owner':
        return (
          <MikeTooltip
            title={intl.formatMessage({ id: `common.roles.${value}` })}
          >
            <UserOwner />
          </MikeTooltip>
        )
      case 'Contributor':
        return <UserContributor />
      default:
        return <UserReader />
    }
  }

  const handlePageChange = (newPage: number) => {
    setPage(newPage)
  }

  const handleRowsPerPageChange = (newRowsPerPage: number) => {
    localStorage.setItem('users-rowsPerPage', String(newRowsPerPage))
    setRowsPerPage(newRowsPerPage)
    setPage(0)
  }

  // Clamp a deep-linked page that no longer exists. Skipped while the list is
  // loading: `sortedUsers` is empty until then, which would read as "page out of
  // range" and drop the `?page=` the user arrived with.
  useEffect(() => {
    if (page === 0 || isLoadingUsers) return
    const totalPages = Math.ceil(sortedUsers.length / rowsPerPage)
    if (page >= totalPages) setPage(0)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sortedUsers, isLoadingUsers])

  const paginatedData = useMemo(() => {
    const startIndex = page * rowsPerPage
    const endIndex = startIndex + rowsPerPage
    return sortedUsers.slice(startIndex, endIndex)
  }, [sortedUsers, page, rowsPerPage])

  return (
    <>
      {!hideNav && <UsersNav />}
      {canViewMemberReport && (
        <MemberReportDialog
          open={memberReportOpen}
          onClose={() => dispatch(setMemberReport({ dialogOpen: false }))}
          tenantId={tenantId}
        />
      )}
      <ConfirmationDialog
        open={openDeleteConfirmation}
        title={intl.formatMessage(localMessages.confirmationDialogTitle, {
          itemCount: itemsToBeDeleted.length
        })}
        message={intl.formatMessage(localMessages.confirmationDialogMessage, {
          itemCount: itemsToBeDeleted.length
        })}
        ok={intl.formatMessage(messages.yesDelete)}
        onOk={onOkDeleteUsers}
        onCancel={onCancelDeleteUsers}
      />
      <StyledTableTopHeader>
        <Typography variant={'h4'}>
          {intl.formatMessage(
            projectIdProp ? localMessages.folderTitle : localMessages.title
          )}
        </Typography>
        {projectIdProp && (
          <Typography variant="body2" color="textSecondary" mt={1}>
            {intl.formatMessage(localMessages.folderDescription)}
          </Typography>
        )}
        <StyledTableTopActionButtons>
          <StyledLeftActionButtons>
            <SearchInput
              id="api-access-search-input"
              placeholder={intl.formatMessage(localMessages.searchPlaceholder)}
              autoFocus
              text={searchText}
              onTextChange={text => {
                setSearchText(text)
                setPage(0)
              }}
            />

            <RefreshButton onClick={onRefresh} />

            {selectedRows.length > 0 && selectedRows.every(canDeleteUser) && (
              <DeleteButton onClick={onDeleteItems} />
            )}
          </StyledLeftActionButtons>
          <StyledRightActionButtons>
            {canViewMemberReport && (
              <IconButton
                sx={{ gap: 1 }}
                messageId="screens.users.memberReport"
                icon={<MemberReportIcon />}
                onClick={() =>
                  dispatch(setMemberReport({ dialogOpen: true, ready: false }))
                }
                size="large"
              />
            )}
            {(!folderScoped || canManageMembers) && (
              <IconButton
                messageId="screens.users.inviteUser"
                onClick={() => {
                  setInviteUserDialogVisible(true)
                }}
                size="large"
              />
            )}
          </StyledRightActionButtons>
        </StyledTableTopActionButtons>
      </StyledTableTopHeader>
      <DataTable
        actions={tableActions}
        loading={isLoadingUsers}
        columns={[
          {
            field: 'name',
            label: intl.formatMessage(messages.name),
            render: projectNameRender,
            className: rowClassName
          },
          {
            field: 'email',
            label: intl.formatMessage(messages.email),
            render: longTextRender,
            className: rowClassName
          },
          {
            field: 'role',
            label: intl.formatMessage(messages.accessLevel),
            render: accessLevelRender,
            className: rowClassName
          }
        ]}
        idField={'id'}
        data={paginatedData}
        onColumnClick={disableRowClick ? undefined : onColumnClick}
        onHandleRequestSort={onHandleRequestSort}
        onSelectionChange={setSelectedRows}
        selectedRows={selectedRows}
        topOffset={0}
        totalCount={sortedUsers.length}
        page={page}
        _rowsPerPage={rowsPerPage}
        onChangePage={handlePageChange}
        onChangeRowsPerPage={handleRowsPerPageChange}
        _order={sortOrder}
        _orderBy={sortOrderBy}
      />
      {formsInDialog ? (
        <Dialog
          open={openUserEdit}
          onClose={() => setOpenUserEdit(false)}
          fullWidth
          maxWidth="sm"
        >
          <DialogTitle
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: 2
            }}
          >
            {intl.formatMessage({ id: 'screens.users.editUser' })}
            <MuiIconButton
              aria-label="Close"
              size="small"
              onClick={() => setOpenUserEdit(false)}
            >
              <Close />
            </MuiIconButton>
          </DialogTitle>
          <DialogContent>
            {currentEditingUser && (
              <UserEditForm
                user={currentEditingUser}
                handleSubmit={(user: ITenantUser) => {
                  setOpenUserEdit(false)
                  effectiveId && dispatch(updateUser(user, effectiveId))
                }}
              />
            )}
          </DialogContent>
        </Dialog>
      ) : (
        <MikeSlidingPanel
          position="right"
          isOpen={openUserEdit}
          onClose={() => {
            setOpenUserEdit(false)
          }}
          titleArea={intl.formatMessage({
            id: 'screens.users.editUser'
          })}
          contentArea={
            currentEditingUser && (
              <UserEditForm
                user={currentEditingUser}
                handleSubmit={(user: ITenantUser) => {
                  setOpenUserEdit(false)
                  effectiveId && dispatch(updateUser(user, effectiveId))
                }}
              />
            )
          }
          actionsArea={null}
          noGrayOverlay={false}
        />
      )}
      {formsInDialog ? (
        <Dialog
          open={inviteUserDialogVisible}
          onClose={() => setInviteUserDialogVisible(false)}
          fullWidth
          maxWidth="sm"
        >
          <DialogTitle
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: 2
            }}
          >
            {intl.formatMessage({ id: 'screens.users.inviteUser' })}
            <MuiIconButton
              aria-label="Close"
              size="small"
              onClick={() => setInviteUserDialogVisible(false)}
            >
              <Close />
            </MuiIconButton>
          </DialogTitle>
          <DialogContent>
            <UserInviteForm
              handleSubmit={(data: UserInviteType) => {
                setInviteUserDialogVisible(false)
                effectiveId &&
                  dispatch(
                    inviteUser(data.email, data.role, data.url, effectiveId)
                  )
              }}
            />
          </DialogContent>
        </Dialog>
      ) : (
        <MikeSlidingPanel
          position="right"
          isOpen={inviteUserDialogVisible}
          onClose={() => {
            setInviteUserDialogVisible(false)
          }}
          titleArea={intl.formatMessage({
            id: 'screens.users.inviteUser'
          })}
          contentArea={
            <UserInviteForm
              handleSubmit={(data: UserInviteType) => {
                setInviteUserDialogVisible(false)
                effectiveId &&
                  dispatch(
                    inviteUser(data.email, data.role, data.url, effectiveId)
                  )
              }}
            />
          }
          actionsArea={null}
          noGrayOverlay={false}
        />
      )}
    </>
  )
}

export default Users
